import * as React from "react";
import { useSearchParams } from "react-router-dom";

import {
  getMessagePathNodeFromRecord,
  MessagePathRecord,
  TopicRecord,
} from "@/shared/domain/topics";
import { SearchParams } from "@/shared/environment";
import { ErrorMonitoringService } from "@/shared/services";
import { useSubscribeToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  ApplyLayoutActionPayload,
  WORKSPACE_ACTION_TOPIC,
  WorkspaceActionPayload,
  WorkspaceActionResult,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import {
  type Dispatch,
  reducer,
  useVizDispatch,
  useVizState,
} from "@/shared/state/visualization";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";
import { nanoSecToSecStr } from "@/shared/time";

import { constructPanelForMessagePath } from "../../DndContainer/dropHandlers";
import {
  SidebarTab,
  SidebarViewType,
  useWorkspaceSidebar,
} from "../../sidebarContext";
import type { EphemeralWorkspaceState } from "../../WorkspaceCtx/EphemeralWorkspaceState";
import {
  handleAddToPanel,
  handleCreatePanel,
  handleRemoveFromPanel,
  handleRemovePanel,
} from "../workspaceActionHandlers";

/**
 * Execute workspace actions published on the PubSubBus, so components outside
 * the workspace (like the AI agent thread sidebar) can drive it without a
 * direct dependency.
 *
 * This allows components outside the workspace (like the AI agent thread sidebar)
 * to trigger workspace actions without direct dependencies.
 *
 * Takes the workspace state as an argument because it runs in the component
 * that provides `EphemeralWorkspaceStateContext`, above the provider. Reading
 * it from context here would return the default state instead, whose
 * `isHydrating` is false — which would hold the gate below permanently open.
 *
 * No subscription exists while the workspace is hydrating, and that absence is
 * itself the signal: `publish` reports false when a topic has no subscriber,
 * so a tool learns to wait and retry rather than being told its panel was
 * applied. Hydration is not merely late in this render — it waits on the
 * workspace's metadata fetch, so the window is seconds wide, and it ends in
 * `replacePanelsAndLayout`, a wholesale swap that destroys anything accepted
 * in the meantime.
 */
export function useWorkspaceActionSubscriber(
  workspaceContext: EphemeralWorkspaceState,
) {
  const [_, setSearchParams] = useSearchParams();
  const vizDispatch = useVizDispatch();
  const vizState = useVizState();
  const sidebar = useWorkspaceSidebar();
  const { activeLayout, isDataLoading, isHydrating, setActiveLayout, timer } =
    workspaceContext;

  // Panel mutations can arrive several per turn (the tool calls of one
  // assistant turn run in a sequential loop with no render in between), so
  // validating against the last-committed `vizState` would let a later
  // mutation reason about pre-mutation state. Track a pending state that
  // advances synchronously with every dispatch and resyncs to the committed
  // state after each render, and validate mutations against it.
  const pendingStateRef = React.useRef(vizState);
  React.useEffect(() => {
    pendingStateRef.current = vizState;
  }, [vizState]);
  const trackedDispatch = React.useCallback<Dispatch>(
    (action) => {
      pendingStateRef.current = reducer(pendingStateRef.current, action);
      vizDispatch(action);
    },
    [vizDispatch],
  );

  const setTimestampParam = React.useCallback(
    (timestamp: bigint) => {
      setSearchParams(
        (oldParams) => {
          const params = new URLSearchParams(oldParams);
          params.set(SearchParams.TIMESTAMP, nanoSecToSecStr(timestamp));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleOpenMessagePath = React.useCallback(
    (
      messagePath: MessagePathRecord,
      topic: TopicRecord,
      timestamp?: bigint,
    ) => {
      if (timestamp !== undefined) {
        setTimestampParam(timestamp);
      }

      const [messagePathNode] = getMessagePathNodeFromRecord(
        topic,
        messagePath,
      );
      if (!messagePathNode) {
        throw new Error(
          `Failed to find message path node for ${messagePath.message_path} when constructing tree from topic ${topic.topic_name} to visualize.`,
        );
      }

      constructPanelForMessagePath(
        vizDispatch,
        messagePathNode,
        determineDefaultPanelType(messagePathNode.data),
        (errMsg) => {
          ErrorMonitoringService.captureError(new Error(errMsg));
        },
      );
    },
    [setTimestampParam, vizDispatch],
  );

  const handleOpenTopicSearch = React.useCallback(
    (topic: TopicRecord, timestamp?: bigint) => {
      if (timestamp !== undefined) {
        setTimestampParam(timestamp);
      }

      sidebar.openView({
        type: SidebarViewType.TabbedSidebar,
        data: {
          initialTopicFilter: {
            term: topic.topic_name,
            key: Date.now(), // Unique key for command semantics
          },
        },
      });
      sidebar.setActiveTab(SidebarTab.Topics);
    },
    [setTimestampParam, sidebar],
  );

  const handleSeekTimer = React.useCallback(
    (timestamp: bigint) => {
      setTimestampParam(timestamp);
      timer.seekTo(timestamp);
    },
    [timer, setTimestampParam],
  );

  // Layout hydration rebuilds panels only when the active layout's id
  // changes, so re-applying the active layout would report success while
  // changing nothing. Compare against the id most recently requested, not the
  // committed one: two applies in one batch see no render in between.
  const requestedLayoutIdRef = React.useRef(activeLayout.layout_id);
  React.useEffect(() => {
    requestedLayoutIdRef.current = activeLayout.layout_id;
  }, [activeLayout.layout_id]);

  const applyLayoutNow = React.useCallback(
    ({ layout, onResult }: ApplyLayoutActionPayload) => {
      if (layout.layout_id === requestedLayoutIdRef.current) {
        onResult({
          applied: false,
          reason: `Layout '${layout.name}' is already the active layout; the panels were left as they are.`,
        });
        return;
      }
      requestedLayoutIdRef.current = layout.layout_id;
      setActiveLayout(layout);
      onResult({ applied: true });
    },
    [setActiveLayout],
  );

  // Hydration treats whatever layout is active when the data load finishes as
  // the starting layout and does not rebuild panels from it, so an apply that
  // arrives mid-load is held and run once the load is done. Only the last one
  // is held; an earlier one is answered as superseded.
  const pendingApplyRef = React.useRef<ApplyLayoutActionPayload | null>(null);
  const handleApplyLayout = React.useCallback(
    (payload: ApplyLayoutActionPayload) => {
      if (!isDataLoading) {
        applyLayoutNow(payload);
        return;
      }
      pendingApplyRef.current?.onResult({
        applied: false,
        reason: `Superseded by a later request to apply layout '${payload.layout.name}'.`,
      });
      pendingApplyRef.current = payload;
    },
    [applyLayoutNow, isDataLoading],
  );
  React.useEffect(() => {
    if (isDataLoading || pendingApplyRef.current === null) {
      return;
    }
    const pending = pendingApplyRef.current;
    pendingApplyRef.current = null;
    applyLayoutNow(pending);
  }, [applyLayoutNow, isDataLoading]);
  React.useEffect(
    () => () => {
      // A held apply must still answer when the workspace unmounts first,
      // or the tool awaiting it never returns.
      pendingApplyRef.current?.onResult({
        applied: false,
        reason: "The visualizer closed before the layout could be applied.",
      });
      pendingApplyRef.current = null;
    },
    [],
  );

  // Panel-mutation handlers are contracted not to throw (failures flow
  // through the result), but the publisher blocks on onResult firing
  // synchronously — so guarantee exactly one result even on an
  // unexpected throw.
  const applyPanelMutation = React.useCallback(
    (
      onResult: (result: WorkspaceActionResult) => void,
      mutate: () => WorkspaceActionResult,
      afterSuccess?: () => void,
    ) => {
      let result: WorkspaceActionResult;
      try {
        result = mutate();
      } catch (error) {
        ErrorMonitoringService.captureError(error);
        result = {
          ok: false,
          error:
            "An unexpected error occurred while applying the workspace action; " +
            "the workspace may have been left partially modified.",
        };
      }
      onResult(result);
      // Side effects that are not part of the mutation (e.g. seeking the
      // timeline) run after the result is sealed, so their failures can
      // never misreport an applied mutation as failed.
      if (result.ok && afterSuccess) {
        try {
          afterSuccess();
        } catch (error) {
          ErrorMonitoringService.captureError(error);
        }
      }
    },
    [],
  );

  const handleWorkspaceAction = React.useCallback(
    (action: WorkspaceActionPayload) => {
      try {
        switch (action.type) {
          case "open_message_path":
            handleOpenMessagePath(
              action.payload.messagePath,
              action.payload.topic,
              action.payload.timestamp,
            );
            break;
          case "open_topic_search":
            handleOpenTopicSearch(
              action.payload.topic,
              action.payload.timestamp,
            );
            break;
          case "seek_timer":
            handleSeekTimer(action.payload.timestamp);
            break;
          case "apply_layout":
            handleApplyLayout(action.payload);
            break;
          case "create_panel": {
            const { payload } = action;
            applyPanelMutation(
              payload.onResult,
              () =>
                handleCreatePanel(
                  pendingStateRef.current,
                  trackedDispatch,
                  payload,
                ),
              () => {
                if (payload.timestamp !== undefined) {
                  setTimestampParam(payload.timestamp);
                }
              },
            );
            break;
          }
          case "add_to_panel": {
            const { payload } = action;
            applyPanelMutation(payload.onResult, () =>
              handleAddToPanel(
                pendingStateRef.current,
                trackedDispatch,
                payload,
              ),
            );
            break;
          }
          case "remove_from_panel": {
            const { payload } = action;
            applyPanelMutation(payload.onResult, () =>
              handleRemoveFromPanel(
                pendingStateRef.current,
                trackedDispatch,
                payload,
              ),
            );
            break;
          }
          case "remove_panel": {
            const { payload } = action;
            applyPanelMutation(payload.onResult, () =>
              handleRemovePanel(
                pendingStateRef.current,
                trackedDispatch,
                payload,
              ),
            );
            break;
          }
          default:
            // Reachable only when a publisher violates the topic's payload
            // convention — that should never happen, so surface it as a bug.
            ErrorMonitoringService.captureError(
              new Error(
                `Unknown workspace action type: ${JSON.stringify(action)}`,
              ),
            );
        }
      } catch (error) {
        ErrorMonitoringService.captureError(error);
      }
    },
    [
      applyPanelMutation,
      handleApplyLayout,
      handleOpenMessagePath,
      handleOpenTopicSearch,
      handleSeekTimer,
      setTimestampParam,
      trackedDispatch,
    ],
  );

  useSubscribeToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
    WORKSPACE_ACTION_TOPIC,
    handleWorkspaceAction,
    !isHydrating,
  );
}
